import React, { useState, useEffect, useCallback } from 'react';

interface Invite {
  code: string;
  role: 'member' | 'admin';
  created_by?: string;
  used_count: number;
  max_uses: number;
  expires_at: string | null;
  revoked: boolean;
  created_at?: string;
}

interface UserManagementPanelProps {
  isOwner?: boolean;
}

export const UserManagementPanel: React.FC<UserManagementPanelProps> = ({ isOwner = true }) => {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [selectedRole, setSelectedRole] = useState<'member' | 'admin'>('member');
  const [expiryDays, setExpiryDays] = useState<number>(7);
  const [maxUses, setMaxUses] = useState<number>(1);
  const [createdCode, setCreatedCode] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [errorNotice, setErrorNotice] = useState<string | null>(null);

  const fetchInvites = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/admin/invites', {
        headers: { 'x-mesh-role': isOwner ? 'mesh_owner' : 'admin' },
      });
      if (res.ok) {
        const data = await res.json();
        setInvites(data.invites || []);
      }
    } catch (e: any) {
      console.warn('[UserManagement] Failed to load invites:', e.message);
    }
  }, [isOwner]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setErrorNotice(null);
    try {
      const res = await fetch('/api/v1/admin/invites', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-mesh-role': isOwner ? 'mesh_owner' : 'admin',
        },
        body: JSON.stringify({
          role: selectedRole,
          expiresInDays: expiryDays,
          maxUses,
        }),
      });

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Server returned invalid response format.');
      }

      const data = await res.json();
      if (data.success && data.invite) {
        setCreatedCode(data.invite.code);
        setCopied(false);
        fetchInvites();
      } else {
        setErrorNotice(data.error || 'Failed to generate invite');
      }
    } catch (e: any) {
      setErrorNotice(e.message || 'Error creating invite');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRevoke = async (code: string) => {
    try {
      const res = await fetch(`/api/v1/admin/invites/${encodeURIComponent(code)}`, {
        method: 'DELETE',
        headers: { 'x-mesh-role': isOwner ? 'mesh_owner' : 'admin' },
      });
      if (res.ok) {
        fetchInvites();
      }
    } catch (e: any) {
      console.warn('[UserManagement] Failed to revoke invite:', e.message);
    }
  };

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', color: '#f8fafc' }}>
      {/* 1. Generate Invite Section */}
      <div
        style={{
          padding: '16px 20px',
          background: 'rgba(15, 23, 42, 0.65)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#38bdf8' }}>
            Generate Sovereign Invite Key
          </div>
          <span style={{ fontSize: '11px', color: '#94a3b8' }}>
            Root Authority: {isOwner ? 'Mesh Box Owner' : 'Admin'}
          </span>
        </div>

        {errorNotice && (
          <div style={{ padding: '8px 12px', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', color: '#fca5a5', fontSize: '12px' }}>
            {errorNotice}
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#94a3b8', marginBottom: '6px' }}>
              Assign Role
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as 'member' | 'admin')}
              style={{
                background: '#1e293b',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                color: '#f8fafc',
                fontSize: '12px',
                borderRadius: '8px',
                padding: '8px 12px',
                outline: 'none',
              }}
            >
              <option value="member">Ordinary Member (Standard Access)</option>
              <option value="admin">Administrator (Full Node Privileges)</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#94a3b8', marginBottom: '6px' }}>
              Expiration Window
            </label>
            <select
              value={expiryDays}
              onChange={(e) => setExpiryDays(Number(e.target.value))}
              style={{
                background: '#1e293b',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                color: '#f8fafc',
                fontSize: '12px',
                borderRadius: '8px',
                padding: '8px 12px',
                outline: 'none',
              }}
            >
              <option value={1}>24 Hours</option>
              <option value={7}>7 Days (Standard)</option>
              <option value={30}>30 Days</option>
              <option value={0}>Never Expires (Permanent Key)</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#94a3b8', marginBottom: '6px' }}>
              Max Uses
            </label>
            <select
              value={maxUses}
              onChange={(e) => setMaxUses(Number(e.target.value))}
              style={{
                background: '#1e293b',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                color: '#f8fafc',
                fontSize: '12px',
                borderRadius: '8px',
                padding: '8px 12px',
                outline: 'none',
              }}
            >
              <option value={1}>1 Use (Single-Use Token)</option>
              <option value={5}>5 Uses</option>
              <option value={20}>20 Uses (Team Batch)</option>
              <option value={9999}>Unlimited Uses</option>
            </select>
          </div>

          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerating}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: 'linear-gradient(135deg, #059669, #10b981)',
              color: '#ffffff',
              fontSize: '12px',
              fontWeight: 600,
              cursor: isGenerating ? 'not-allowed' : 'pointer',
              opacity: isGenerating ? 0.6 : 1,
              transition: 'all 0.15s ease',
            }}
          >
            {isGenerating ? 'Generating...' : '+ Create Invite Code'}
          </button>
        </div>

        {createdCode && (
          <div
            style={{
              marginTop: '8px',
              padding: '12px 16px',
              background: 'rgba(5, 150, 105, 0.15)',
              border: '1px solid rgba(16, 185, 129, 0.35)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <span style={{ fontSize: '11px', color: '#6ee7b7', fontWeight: 600 }}>New Invite Key Ready:</span>
              <div style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: 700, letterSpacing: '1px', color: '#a7f3d0', marginTop: '2px' }}>
                {createdCode}
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleCopy(createdCode)}
              style={{
                padding: '6px 14px',
                borderRadius: '6px',
                border: '1px solid rgba(16, 185, 129, 0.5)',
                background: 'rgba(5, 150, 105, 0.3)',
                color: '#ecfdf5',
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {copied ? '✓ Copied' : 'Copy Key'}
            </button>
          </div>
        )}
      </div>

      {/* 2. Invites Registry Table */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>
            Invite Codes Registry ({invites.length})
          </div>
          <button
            type="button"
            onClick={fetchInvites}
            style={{ background: 'transparent', border: 'none', color: '#38bdf8', fontSize: '12px', cursor: 'pointer' }}
          >
            ↻ Refresh
          </button>
        </div>

        <div style={{ border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '10px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12px' }}>
            <thead style={{ background: 'rgba(0, 0, 0, 0.35)', color: '#94a3b8', textTransform: 'uppercase', fontSize: '10px', letterSpacing: '0.05em' }}>
              <tr>
                <th style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>Invite Code</th>
                <th style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>Role</th>
                <th style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>Usage</th>
                <th style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>Status</th>
                <th style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody style={{ background: 'rgba(15, 23, 42, 0.35)' }}>
              {invites.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                    No dynamic invite keys registered yet. Create one above to onboard peers.
                  </td>
                </tr>
              ) : (
                invites.map((inv) => {
                  const isExhausted = inv.used_count >= inv.max_uses;
                  const isExpired = inv.expires_at ? new Date(inv.expires_at) < new Date() : false;
                  const isActive = !inv.revoked && !isExhausted && !isExpired;

                  return (
                    <tr key={inv.code} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.04)' }}>
                      <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontWeight: 600, color: '#e2e8f0' }}>
                        {inv.code}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            background: inv.role === 'admin' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(56, 189, 248, 0.15)',
                            color: inv.role === 'admin' ? '#f59e0b' : '#38bdf8',
                          }}
                        >
                          {inv.role}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', color: '#94a3b8' }}>
                        {inv.used_count} / {inv.max_uses >= 9999 ? '∞' : inv.max_uses}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        {isActive ? (
                          <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '10px', fontWeight: 700, background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', color: '#34d399' }}>
                            ACTIVE
                          </span>
                        ) : inv.revoked ? (
                          <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '10px', fontWeight: 700, background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#f87171' }}>
                            REVOKED
                          </span>
                        ) : isExpired ? (
                          <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '10px', fontWeight: 700, background: 'rgba(148, 163, 184, 0.15)', color: '#94a3b8' }}>
                            EXPIRED
                          </span>
                        ) : (
                          <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '10px', fontWeight: 700, background: 'rgba(148, 163, 184, 0.15)', color: '#94a3b8' }}>
                            EXHAUSTED
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => handleCopy(inv.code)}
                            style={{ background: 'transparent', border: 'none', color: '#38bdf8', fontSize: '11px', cursor: 'pointer', fontWeight: 600 }}
                          >
                            Copy
                          </button>
                          {isActive && (
                            <button
                              type="button"
                              onClick={() => handleRevoke(inv.code)}
                              style={{ background: 'transparent', border: 'none', color: '#f87171', fontSize: '11px', cursor: 'pointer', fontWeight: 600 }}
                            >
                              Revoke
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
